import React, { memo, useMemo } from "react";
import { LuChevronRight, LuChevronLeft } from "react-icons/lu";
import FilterSelect from "../Atoms/FilterSelect/FilterSelect";

const pageButtonBase =
  "inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-3 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--admin-blue)]/20 disabled:cursor-not-allowed disabled:opacity-45";

const pageButtonIdle =
  "border-[var(--admin-line)] bg-white text-[var(--admin-muted)] hover:border-[var(--admin-blue)]/40 hover:bg-[var(--admin-blue-soft)] hover:text-[var(--admin-blue)]";

const pageButtonActive =
  "border-[var(--admin-navy)] bg-[var(--admin-navy)] text-white shadow-sm shadow-[var(--admin-navy)]/20";

const Pagination = ({
  totalPages = 1,
  currentPage = 1,
  onPageChange,
  totalRecords,
  pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  compact = false,
}) => {
  const safeTotalPages = Math.max(1, Number(totalPages) || 1);

  const safeCurrentPage = Math.min(
    Math.max(1, Number(currentPage) || 1),
    safeTotalPages,
  );

  const handlePageChange = (page) => {
    if (typeof page !== "number" || page === safeCurrentPage) return;

    if (page >= 1 && page <= safeTotalPages) {
      onPageChange?.(page);
    }
  };

  const options = useMemo(
    () =>
      pageSizeOptions.map((size) => ({
        value: size,
        label: String(size),
      })),
    [pageSizeOptions],
  );

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const isTruncated = safeTotalPages > maxPagesToShow;

    for (let i = 1; i <= Math.min(2, safeTotalPages); i++) {
      pageNumbers.push(i);
    }

    if (isTruncated && safeCurrentPage > 4) {
      pageNumbers.push("...");
    }

    for (
      let i = Math.max(3, safeCurrentPage - 1);
      i <= Math.min(safeTotalPages - 2, safeCurrentPage + 1);
      i++
    ) {
      pageNumbers.push(i);
    }

    if (isTruncated && safeCurrentPage < safeTotalPages - 3) {
      pageNumbers.push("...");
    }

    for (let i = Math.max(safeTotalPages - 1, 3); i <= safeTotalPages; i++) {
      if (i > 2) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const pageNumbers = renderPageNumbers();

  const rangeStart =
    totalRecords && pageSize ? (safeCurrentPage - 1) * pageSize + 1 : 0;

  const rangeEnd =
    totalRecords && pageSize
      ? Math.min(safeCurrentPage * pageSize, totalRecords)
      : 0;

  const wrapperClass = compact
    ? "admin-pagination flex flex-wrap items-center justify-start gap-3"
    : "admin-pagination flex w-full flex-wrap items-center justify-between gap-3 rounded-lg px-3 py-2";

  return (
    <div className={wrapperClass}>
      {/* Showing Count - Always at Start */}
      {!compact && totalRecords !== undefined && pageSize && (
        <div className="flex shrink-0 flex-col leading-tight">
          <span className="text-xs font-medium text-[var(--admin-muted)]">
            Showing {rangeStart}-{rangeEnd} of {totalRecords}
          </span>
        </div>
      )}

      {/* Right Side Controls */}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
        {/* Rows Per Page */}
        {onPageSizeChange && (
          <div className="inline-flex items-center gap-2 rounded-md bg-[var(--admin-surface-soft)] px-2 py-1 text-xs font-medium text-[var(--admin-muted)]">
            <span className="whitespace-nowrap">Rows Per Page</span>

            <FilterSelect
              options={options}
              value={
                options.find((option) => option.value === pageSize) || null
              }
              onChange={(option) => onPageSizeChange(Number(option?.value))}
              isSearchable={false}
              placeholder={String(pageSize)}
              className="!mb-0 !min-w-0 w-[80px] [&>div]:!min-w-0"
            />
          </div>
        )}

        {/* Pagination Buttons */}
        <nav
          className="flex flex-row items-center justify-end gap-1 overflow-x-auto"
          aria-label="Pagination"
        >
          {/* First */}
          <button
            type="button"
            className={`${pageButtonBase} ${pageButtonIdle} hidden sm:inline-flex`}
            onClick={() => handlePageChange(1)}
            disabled={safeCurrentPage === 1}
            aria-label="First page"
          >
            First
          </button>

          {/* Previous */}
          <button
            type="button"
            className={`${pageButtonBase} ${pageButtonIdle} !px-0`}
            onClick={() => handlePageChange(safeCurrentPage - 1)}
            disabled={safeCurrentPage === 1}
            aria-label="Previous page"
          >
            <LuChevronLeft aria-hidden="true" />
          </button>

          {/* Page Numbers */}
          {pageNumbers.map((page, index) => (
            <button
              type="button"
              key={`${page}-${index}`}
              className={`${pageButtonBase} ${
                safeCurrentPage === page ? pageButtonActive : pageButtonIdle
              } ${
                page === "..."
                  ? "!min-w-7 !border-transparent !bg-transparent !px-1 !text-slate-400 !opacity-100"
                  : ""
              }`}
              onClick={() => handlePageChange(page)}
              disabled={page === "..."}
              aria-current={safeCurrentPage === page ? "page" : undefined}
              aria-label={page === "..." ? "More pages" : `Page ${page}`}
            >
              {page}
            </button>
          ))}

          {/* Next */}
          <button
            type="button"
            className={`${pageButtonBase} ${pageButtonIdle} !px-0`}
            onClick={() => handlePageChange(safeCurrentPage + 1)}
            disabled={safeCurrentPage === safeTotalPages}
            aria-label="Next page"
          >
            <LuChevronRight aria-hidden="true" />
          </button>

          {/* Last */}
          <button
            type="button"
            className={`${pageButtonBase} ${pageButtonIdle} hidden sm:inline-flex`}
            onClick={() => handlePageChange(safeTotalPages)}
            disabled={safeCurrentPage === safeTotalPages}
            aria-label="Last page"
          >
            Last
          </button>
        </nav>
      </div>
    </div>
  );
};

export default memo(Pagination);
